// src/pages/ProductDetailPage.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useLocale } from "../contexts/LocaleContext";
import {
  getProductDetailPageTitles,
  getProductItemDetails,
} from "../api/apiService";
import PageBanner from "../components/common/PageBanner";
import RichTextRenderer from "../components/common/RichTextRenderer";
import pdfIcon from "../assets/pdf.png";

const DownloadArrowIcon = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
    />
  </svg>
);

function ProductDetailPage() {
  const { productName } = useParams();
  const { locale } = useLocale();

  const [pageTitles, setPageTitles] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageContent = async () => {
      if (!productName) {
        setError("Product name not found in URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [titlesData, itemData] = await Promise.all([
          getProductDetailPageTitles(locale),
          getProductItemDetails(productName, locale),
        ]);

        setPageTitles(titlesData);
        setProductDetails(itemData);

        if (!itemData) {
          setError(`Product details not found for "${productName}".`);
        }
      } catch (err) {
        console.error(
          `[ProductDetailPage] Failed to fetch data for product "${productName}":`,
          err
        );
        setError(err.message || "Failed to load product content.");
        setPageTitles(null);
        setProductDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPageContent();
  }, [productName, locale]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }
  if (!productDetails || !pageTitles) {
    const message = error
      ? error
      : `Product "${productName}" not found or content is unavailable.`;
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">{message}</p>
      </div>
    );
  }

  const fileSize = (size) => {
    if (size < 1024) return `${size.toFixed(0)} KB`;
    return `${(size / 1024).toFixed(2)} MB`;
  };

  const {
    banner,
    youtube_id,
    fearures_content,
    features_images,
    downloads_files,
  } = productDetails;
  const { features_title, downloads_title } = pageTitles;

  return (
    <div className="bg-white">
      <PageBanner
        title={banner?.title}
        imageUrl={banner?.image?.url}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        bannerHeightClass="h-52 sm:h-60 md:h-72 lg:h-80"
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {youtube_id && (
          <div className="mb-10 md:mb-16 aspect-video max-w-3xl mx-auto shadow-xl rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${youtube_id}`}
              title="Product Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* 3. Features Section */}
        <section className="mb-10 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8 text-center md:text-left py-3 bg-[#003366] text-white px-4 rounded-md">
            {features_title || "Features"}
          </h2>
          <div className="flex flex-col md:flex-row gap-8 md:gap-10 lg:gap-12">
            <div className="md:w-2/3 prose prose-base sm:prose-lg max-w-none text-gray-700 content-evenly">
              {fearures_content && fearures_content.length > 0 ? (
                <RichTextRenderer content={fearures_content} />
              ) : (
                <p>No features described.</p>
              )}
            </div>
            {features_images && features_images.length > 0 && (
              <div className="md:w-1/4 content-center">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 gap-4 content-evenly">
                  {features_images.map((img) => (
                    <div>
                      <div
                        key={img.image.id}
                        className=" rounded-lg shadow-md overflow-hidden aspect-[5/3]"
                      >
                        <img
                          src={`${img.image.url}`}
                          alt={
                            img.image.alternativeText ||
                            img.image.name ||
                            "Feature image"
                          }
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                      {img.subtitle && (
                        <p className="text-center text-base md:text-lg text-gray-800 mt-3">
                          {img.subtitle}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 4. Downloads Section */}
        {downloads_files && downloads_files.length > 0 && (
          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 md:mb-8 text-center md:text-left py-3 bg-[#003366] text-white px-4 rounded-md">
              {downloads_title || "Downloads:"}
            </h2>
            <ul className="space-y-4">
              {downloads_files.map(
                (fileItem) =>
                  fileItem.download_file &&
                  fileItem.download_file.url && (
                    <li
                      key={fileItem.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <a
                        href={`${fileItem.download_file.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between text-blue-700 hover:text-blue-900 group"
                      >
                        <div className="flex flex-row items-center mr-4">
                          {" "}
                          <span className="font-medium text-lg md:text-xl group-hover:underline block">
                            {" "}
                            {fileItem.download_title ||
                              fileItem.download_file.name}
                          </span>
                          {fileItem.download_file.size && (
                            <span className="block text-xs sm:text-sm text-gray-500 hidden md:pl-2 md:flex">
                              ({fileSize(fileItem.download_file.size)})
                            </span>
                          )}
                        </div>

                        <div className="flex items-center space-x-3 md:space-x-5 flex-shrink-0 mr-2 md:mr-5">
                          <DownloadArrowIcon className="w-6 h-6 md:w-8 md:h-8 text-red-800 group-hover:text-red-700 transition-colors" />
                          <img
                            src={pdfIcon}
                            className="w-7 md:w-9 text-red-600 flex-shrink-0"
                          />
                        </div>
                      </a>
                    </li>
                  )
              )}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

export default ProductDetailPage;
